import React, { useEffect, useState } from 'react';
import ComposeDocumentsPanel from './ComposeDocumentsPanel';
import PageSelectionPanel from './PageSelectionPanel';
import styled from 'styled-components';
import OrganizePagesHeader from './OrganizePagesHeader';
import {
  BulkComposeOperationResponse,
  ComposeDocument,
  SourceDocument,
  ComposeDocumentType,
  SourceDocumentType,
  SourcePage,
} from './types/compose-types';
import {
  buildPageRange,
  getPageRange,
  getRotationDetails,
  hasValidPageRanges,
  isSelectedPage,
  isValidPageRange,
} from './utils/page-range-utils';
import api from './lib/api';
import {
  buildSourceDocument,
  calculateSelectOrder,
  getActiveSourceDocument,
  updateDeselectedPages,
  isValidDocumentName,
} from './utils/compose-documents-utils';
import { useFetchDocuments } from 'zdocs-web-core/hooks/useFetchDocuments';
import { ToastProvider, Toast } from '@zillow/constellation';
import { withToast, WithToastProps } from 'zdocs-web-core/components/withToast';

let __id__ = 0;
function uniqueId() {
  __id__++;
  return `${__id__}`;
}

const SplitViewContainer = styled.div`
  display: flex;
  height: calc(100vh - 70px);
`;

const LoadingContainer = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

interface OrganizePagesErrorProps extends WithToastProps {
  errorMessage: string;
}
const OrganizePagesError = withToast(function OrganizePagesError({
  errorMessage,
  enqueueToast,
}: OrganizePagesErrorProps) {
  useEffect(() => {
    if (errorMessage) {
      enqueueToast(<Toast appearance="error" body={errorMessage} />);
    }
  }, [errorMessage, enqueueToast]);

  return null;
});

type OrganizePagesProps = {
  token: string;
  onCancel: () => void;
  onSave: (
    bulkComposeOperationResponseDto: BulkComposeOperationResponse
  ) => void;
  onSaveError: (
    bulkComposeOperationResponseDto: BulkComposeOperationResponse,
    statusCode: number | undefined
  ) => void;
};

const ZDocsOrganizePages = function ({
  token,
  onCancel,
  onSave,
  onSaveError,
}: OrganizePagesProps) {
  const [errorMessage, setErrorMessage] = useState('');
  const documentList = useFetchDocuments(token);
  const [activeComposeId, setActiveComposeId] = useState<string | null>(null);
  const [composeDocumentList, setComposeDocumentList] = useState<
    Array<ComposeDocumentType>
  >([]);
  const [lastSelectedPage, setLastSelectedPage] = useState<SourcePage | null>(
    null
  );

  useEffect(() => {
    if (documentList && documentList.length > 0) {
      const sourceDocuments = documentList.map(doc =>
        buildSourceDocument(doc, documentList[0].id === doc.id)
      );
      const id = uniqueId();
      setActiveComposeId(id);
      setComposeDocumentList([
        {
          id,
          name: '',
          sourceDocuments,
        },
      ]);
    }
  }, [documentList]);

  const addNewComposeDocument = function () {
    const sourceDocuments = documentList.map(doc =>
      buildSourceDocument(doc, documentList[0].id === doc.id)
    );
    const composeDocId = uniqueId();
    const composeDocument: ComposeDocumentType = {
      id: composeDocId,
      name: '',
      sourceDocuments,
    };
    setErrorMessage('');
    setActiveComposeId(composeDocId);
    setComposeDocumentList(c => [...c, composeDocument]);
  };

  const deleteComposeDocument = function (composeDocId: string) {
    if (composeDocumentList.length === 1) {
      setErrorMessage('Must have at least one document to create.');
    } else {
      const deleteIndex = composeDocumentList.findIndex(
        composeDoc => composeDoc.id === composeDocId
      );
      const newActiveDocIndex = deleteIndex === 0 ? 1 : deleteIndex - 1;
      const activeDocId = composeDocumentList[newActiveDocIndex].id;

      setActiveComposeId(activeDocId);
      setComposeDocumentList(c =>
        c.filter(compDoc => compDoc.id !== composeDocId)
      );
    }
  };

  const changeActiveComposeDocument = function (composeDocumentId: string) {
    setActiveComposeId(composeDocumentId);
  };

  const handleNameChange = function (documentName: string) {
    setComposeDocumentList(c =>
      c.map(compDoc => ({
        ...compDoc,
        name: compDoc.id === activeComposeId ? documentName : compDoc.name,
      }))
    );
  };

  const handlePageRotation = function (page: SourcePage) {
    const updatedList = composeDocumentList.map(compDoc => {
      const sourceDocIndex = compDoc.sourceDocuments.findIndex(
        sourceDoc => sourceDoc.documentId === page.documentId
      );
      if (sourceDocIndex > -1) {
        const sourceDoc = compDoc.sourceDocuments[sourceDocIndex];
        const updatedSourcePages = sourceDoc.sourcePages.map(sourcePage => {
          if (sourcePage.pageNumber === page.pageNumber) {
            let rotation = 0;
            if (sourcePage.rotation === 270) {
              rotation = 0;
            } else {
              rotation = sourcePage.rotation + 90;
            }
            return { ...sourcePage, rotation };
          } else {
            return sourcePage;
          }
        });
        const updatedSourceDoc = {
          documentId: sourceDoc.documentId,
          name: sourceDoc.name,
          sourcePages: updatedSourcePages,
          selectedPages: sourceDoc.selectedPages,
          isActive: sourceDoc.isActive,
        };
        const sourceDocumentList = compDoc.sourceDocuments.slice();
        sourceDocumentList[sourceDocIndex] = updatedSourceDoc;
        return { ...compDoc, sourceDocuments: sourceDocumentList };
      } else {
        return { ...compDoc };
      }
    });
    setComposeDocumentList(updatedList);
  };

  const handlePageSelection = function (page: SourcePage, shift: boolean) {
    const activeSourceDoc = getActiveSourceDocument(
      composeDocumentList,
      activeComposeId
    );

    if (!activeSourceDoc) {
      return;
    }
    let updatedPages: Array<SourcePage> = [];
    if (
      isSelectedPage(
        activeSourceDoc.sourcePages,
        page.documentId,
        page.pageNumber
      )
    ) {
      updatedPages = updateDeselectedPages(activeSourceDoc, page);
    } else {
      updatedPages = updateSelectedPages(activeSourceDoc, page, shift);
    }
    updateSelectedPageState(updatedPages);
  };

  const updateSelectedPageState = function (updatedPages: Array<SourcePage>) {
    setComposeDocumentList(c =>
      c.map(compDoc => {
        if (compDoc.id === activeComposeId) {
          return {
            ...compDoc,
            sourceDocuments: compDoc.sourceDocuments.map(sourceDoc => {
              if (sourceDoc.isActive) {
                const newSourcePages = sourceDoc.sourcePages.map(sourcePage => {
                  const selectedPage = updatedPages.find(
                    selectedPage =>
                      selectedPage.documentId === sourcePage.documentId &&
                      selectedPage.pageNumber === sourcePage.pageNumber
                  );
                  if (selectedPage) {
                    return selectedPage;
                  }
                  return sourcePage;
                });
                const result = {
                  ...sourceDoc,
                  sourcePages: newSourcePages,
                  selectedPages: buildPageRange(
                    newSourcePages,
                    sourceDoc.documentId
                  ),
                };

                return result;
              }
              return sourceDoc;
            }),
          };
        }
        return compDoc;
      })
    );
  };

  const updateSelectedPages = function (
    activeSourceDoc: SourceDocumentType,
    selectedPage: SourcePage,
    shift: boolean
  ): Array<SourcePage> {
    const updatedPages: Array<SourcePage> = [];
    let nextSelectedOrder = calculateSelectOrder(activeSourceDoc.sourcePages);
    if (shift && lastSelectedPage) {
      if (lastSelectedPage) {
        const startPage = lastSelectedPage as SourcePage;
        const startPageNum = startPage.pageNumber;
        const endPageNum = selectedPage.pageNumber;
        if (startPage.pageNumber > selectedPage.pageNumber) {
          for (let j = startPageNum; j >= endPageNum; j--) {
            selectSourcePage(
              j,
              nextSelectedOrder++,
              activeSourceDoc,
              updatedPages
            );
          }
        } else {
          for (let j = startPage.pageNumber; j <= endPageNum; j++) {
            selectSourcePage(
              j,
              nextSelectedOrder++,
              activeSourceDoc,
              updatedPages
            );
          }
        }
        setLastSelectedPage(selectedPage);
      }
    } else {
      setLastSelectedPage(selectedPage);
      selectSourcePage(
        selectedPage.pageNumber,
        nextSelectedOrder,
        activeSourceDoc,
        updatedPages
      );
    }
    return updatedPages;
  };

  const selectSourcePage = function (
    pageNumber: number,
    nextSelectedOrder: number,
    activeSourceDoc: SourceDocumentType,
    updatedPages: Array<SourcePage>
  ) {
    const selectPage = activeSourceDoc.sourcePages.find(
      sourcePage =>
        sourcePage.pageNumber === pageNumber &&
        sourcePage.documentId === activeSourceDoc.documentId
    );
    if (selectPage && !selectPage.selected) {
      updatedPages.push({
        ...selectPage,
        selected: true,
        selectedOrder: nextSelectedOrder,
      });
    }
  };

  const handleSelectAllPages = function () {
    const activeSourceDoc = getActiveSourceDocument(
      composeDocumentList,
      activeComposeId
    );
    if (!activeSourceDoc) {
      return;
    }
    const startPage = activeSourceDoc.sourcePages[0].pageNumber;
    const endPage =
      activeSourceDoc.sourcePages[activeSourceDoc.sourcePages.length - 1]
        .pageNumber;
    let nextSelectedOrder = calculateSelectOrder(activeSourceDoc.sourcePages);
    const updatedPages: Array<SourcePage> = [];
    for (let j = startPage; j <= endPage; j++) {
      selectSourcePage(j, nextSelectedOrder++, activeSourceDoc, updatedPages);
    }
    updateSelectedPageState(updatedPages);
  };

  const handlePageRangesChange = function (selectedPageRanges: string) {
    const activeSourceDoc = getActiveSourceDocument(
      composeDocumentList,
      activeComposeId
    );
    if (activeSourceDoc) {
      const sourceDocumentId = activeSourceDoc.documentId;
      const selectedPageRangesArray = selectedPageRanges.split(',');
      const isValid = isValidPageRange(
        selectedPageRanges,
        activeSourceDoc.sourcePages.length
      );
      const selectedDocumentPageNumbers: Record<string, number | undefined> =
        {};
      let order = 1;
      for (let i = 0; i < selectedPageRangesArray.length; i++) {
        const pageRange = getPageRange(selectedPageRangesArray[i]);
        if (pageRange.endPage) {
          for (let j = pageRange.startPage; j <= pageRange.endPage; j++) {
            selectedDocumentPageNumbers[j] = order++;
          }
        } else {
          selectedDocumentPageNumbers[pageRange.startPage] = order++;
        }
      }

      setComposeDocumentList(c =>
        c.map(compDoc => {
          if (compDoc.id === activeComposeId) {
            return {
              ...compDoc,
              sourceDocuments: compDoc.sourceDocuments.map(s => {
                if (s.isActive) {
                  const newSourcePages = s.sourcePages.map(sourcePage => ({
                    ...sourcePage,
                    selected:
                      !!selectedDocumentPageNumbers[sourcePage.pageNumber],
                    selectedOrder:
                      selectedDocumentPageNumbers[sourcePage.pageNumber] ?? 0,
                  }));
                  const result = {
                    ...s,
                    sourcePages: newSourcePages,
                    selectedPages: isValid
                      ? buildPageRange(newSourcePages, sourceDocumentId)
                      : selectedPageRanges,
                  };
                  return result;
                }

                return s;
              }),
            };
          }

          return compDoc;
        })
      );
    }
  };

  const validateSave = function () {
    let hasError = false;
    for (let j = 0; j < composeDocumentList.length; j++) {
      if (
        !isValidDocumentName(composeDocumentList[j]) ||
        !hasValidPageRanges(composeDocumentList[j])
      ) {
        hasError = true;
        break;
      }
    }

    if (hasError) {
      setErrorMessage('Please fix the errors to proceed.');
    }
    return !hasError;
  };

  const handleSave = async function () {
    if (validateSave()) {
      const composeDocuments: Array<ComposeDocument> = [];
      composeDocumentList.forEach(composeDoc => {
        const sourceDocuments: Array<SourceDocument> = [];
        for (let j = 0; j < composeDoc.sourceDocuments.length; j++) {
          const { selectedPages, sourcePages, documentId } =
            composeDoc.sourceDocuments[j];
          if (selectedPages.length > 0) {
            const selectedPageRanges = selectedPages.split(',');
            for (let i = 0; i < selectedPageRanges.length; i++) {
              const pageRange = getPageRange(selectedPageRanges[i]);
              const rotationDetails = getRotationDetails(
                sourcePages,
                documentId,
                pageRange
              );
              const startPage = pageRange.startPage;
              const endPage = pageRange.endPage ?? pageRange.startPage;
              sourceDocuments.push({
                documentId: documentId,
                startPage,
                endPage,
                rotationDetails,
              });
            }
          }
        }
        const composeDocument: ComposeDocument = {
          documentName: composeDoc.name,
          sourceDocuments,
        };
        composeDocuments.push(composeDocument);
      });

      try {
        const response = await api.composeDocuments(token, composeDocuments);

        if (response.response?.status === 200) {
          onSave(response.json);
        } else {
          onSaveError(response.json, response.response?.status);
        }
      } catch (e) {}
    }
  };

  const activeSourceDoc = getActiveSourceDocument(
    composeDocumentList,
    activeComposeId
  );

  if (!documentList || documentList.length === 0 || !activeSourceDoc) {
    return <LoadingContainer>Loading...</LoadingContainer>;
  }

  return (
    <ToastProvider portal={false} placement="top">
      <OrganizePagesError errorMessage={errorMessage} />
      <OrganizePagesHeader
        documentName={activeSourceDoc.name}
        onSave={handleSave}
        onCancel={onCancel}
      />
      <SplitViewContainer>
        <ComposeDocumentsPanel
          composeDocumentList={composeDocumentList}
          activeComposeId={activeComposeId}
          pages={activeSourceDoc.sourcePages}
          onAddComposeDocument={addNewComposeDocument}
          onComposeDocumentSelection={changeActiveComposeDocument}
          onPageRangesChange={handlePageRangesChange}
          onNameChange={handleNameChange}
          onDeleteComposeDocument={deleteComposeDocument}
          hasError={!!errorMessage}
        />
        <PageSelectionPanel
          documentId={activeSourceDoc.documentId}
          documentName={activeSourceDoc.name}
          sourcePages={activeSourceDoc.sourcePages}
          onPageSelected={handlePageSelection}
          onPageRotation={handlePageRotation}
          onSelectAllPages={handleSelectAllPages}
        />
      </SplitViewContainer>
    </ToastProvider>
  );
};

export default ZDocsOrganizePages;
